<template>
  <div>
    <!-- This component handles countdown logic and emits events -->
    <!-- The visual countdown display is handled by parent components -->
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, defineProps, defineEmits, defineExpose } from 'vue'
import moment from 'moment'

const props = defineProps({
  session: {
    type: Object,
    required: true
  },
  isSessionEnded: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits([
  'session-started',
  'countdown-update',
  'about-to-start',
  'time-until-start-update'
])

// Session start countdown timer
const sessionStartCountdown = ref(null)
let sessionStartTimer = null
let sessionStartTimeout = null
let sessionTimeCheckInterval = null
let fallbackTimeout = null
let safetyTimeout = null

// Flag to prevent multiple video start calls
const videoStarted = ref(false)

// Separate flag to track session start state for better reactivity
const sessionStarted = ref(false)

// Computed properties
const isSessionNotStarted = computed(() => {
  return !sessionStarted.value && props.session?.is_started !== true
})

const isSessionAboutToStart = computed(() => {
  if (!props.session?.start_date || props.isSessionEnded) return false
  
  try {
    const startTime = moment(props.session.start_date)
    const now = moment()
    const diffMinutes = startTime.diff(now, 'minutes')
    
    // Return true if session starts within 10 minutes
    return diffMinutes <= 10 && diffMinutes > 0
  } catch (error) {
    console.error('Error calculating session start time:', error)
    return false
  }
})

const shouldStartVideo = computed(() => {
  if (!props.session?.start_date || props.isSessionEnded) return false
  
  try {
    const startTime = moment(props.session.start_date)
    const now = moment()
    
    // Return true if current time is past or equal to start time
    return now.isSameOrAfter(startTime)
  } catch (error) {
    console.error('Error checking if should start video:', error)
    return false
  }
})

// Helper function to get time until session starts
const getTimeUntilStart = () => {
  if (!props.session?.start_date) return 'Time not specified'
  
  try {
    const startTime = moment(props.session.start_date)
    const now = moment()
    const diff = startTime.diff(now)
    
    if (diff <= 0) return 'Starting now...'
    
    const duration = moment.duration(diff)
    const days = Math.floor(duration.asDays())
    const hours = duration.hours()
    const minutes = duration.minutes()
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`
    } else {
      return `${minutes}m`
    }
  } catch (error) {
    console.error('Error calculating time until start:', error)
    return 'Time not specified'
  }
}

// Helper function to calculate precise milliseconds until session start
const getMillisecondsUntilStart = () => {
  if (!props.session?.start_date) return null
  
  try {
    const startTime = moment(props.session.start_date)
    const now = moment()
    const diff = startTime.diff(now)
    
    // Return null if session has already started or ended
    if (diff <= 0) return null
    
    return diff
  } catch (error) {
    console.error('Error calculating milliseconds until start:', error)
    return null
  }
}

// Function to validate session timing and handle edge cases
const validateSessionTiming = () => {
  if (!props.session?.start_date) {
    console.warn('Session start date not available')
    return false
  }
  
  try {
    const startTime = moment(props.session.start_date)
    const now = moment()
    
    // Check if start time is valid
    if (!startTime.isValid()) {
      console.error('Invalid session start date format')
      return false
    }
    
    // Check if session has already started
    if (now.isSameOrAfter(startTime)) {
      return 'started'
    }
    
    // Check if session is too far in the future (more than 24 hours)
    const diffHours = startTime.diff(now, 'hours')
    if (diffHours > 24) {
      console.warn('Session is more than 24 hours in the future')
      return 'countdown'
    }
    
    return 'valid'
  } catch (error) {
    console.error('Error validating session timing:', error)
    return false
  }
}

// Function to clear all session timers
const clearSessionTimers = () => {
  if (sessionStartTimer) {
    clearInterval(sessionStartTimer)
    sessionStartTimer = null
  }
  
  if (sessionStartTimeout) {
    clearTimeout(sessionStartTimeout)
    sessionStartTimeout = null
  }
  
  if (sessionTimeCheckInterval) {
    clearInterval(sessionTimeCheckInterval)
    sessionTimeCheckInterval = null
  }
  
  if (fallbackTimeout) {
    clearTimeout(fallbackTimeout)
    fallbackTimeout = null
  }
  
  if (safetyTimeout) {
    clearTimeout(safetyTimeout)
    safetyTimeout = null
  }
  
  sessionStartCountdown.value = null
}

// Function to start periodic time check for accuracy
const startTimeAccuracyCheck = () => {
  if (sessionTimeCheckInterval) return
  
  // Check every 10 seconds to ensure timing accuracy
  sessionTimeCheckInterval = setInterval(() => {
    if (videoStarted.value || !props.session?.start_date) {
      clearInterval(sessionTimeCheckInterval)
      sessionTimeCheckInterval = null
      return
    }
    
    // Verify our timeout is still accurate
    const currentTimeUntilStart = getMillisecondsUntilStart()
    
    if (currentTimeUntilStart === null) {
      // Session should start immediately
      console.log('Session start time reached, starting video immediately')
      clearSessionTimers()
      startVideo()
    } else if (currentTimeUntilStart < 1000) {
      // Session should start within 1 second, clear timers and start
      console.log('Session starting within 1 second, clearing timers and starting video')
      clearSessionTimers()
      startVideo()
    } else if (currentTimeUntilStart < 5000) {
      // Session starting within 5 seconds, log more frequently
      console.log(`Session starting soon: ${Math.ceil(currentTimeUntilStart / 1000)}s until start`)
    } else {
      // Log timing accuracy for debugging
      console.log(`Session timing check: ${Math.ceil(currentTimeUntilStart / 1000)}s until start`)
    }
    
    // Emit time until start update
    emit('time-until-start-update', getTimeUntilStart())
  }, 10000) // Check every 10 seconds
}

// Function to force start video if stuck
const forceStartVideo = () => {
  if (videoStarted.value) return
  
  console.log('Force starting video due to stuck state')
  clearSessionTimers()
  startVideo()
}

// Start session countdown timer
const startSessionCountdown = () => {
  if (!props.session?.start_date || props.isSessionEnded || videoStarted.value || sessionStartTimer || sessionStartTimeout) return
  
  try {
    // Validate session timing first
    const timingStatus = validateSessionTiming()
    
    if (timingStatus === false) {
      console.error('Session timing validation failed')
      return
    }
    
    if (timingStatus === 'started') {
      // Session should start immediately
      console.log('Session already started, starting video immediately')
      startVideo()
      return
    }
    
    if (timingStatus === 'future') {
      // Session is too far in the future, don't start countdown
      console.log('Session is too far in the future, countdown not started')
      return
    }
    
    // Calculate exact milliseconds until session start
    const millisecondsUntilStart = getMillisecondsUntilStart()
    
    if (millisecondsUntilStart === null) {
      // Session should start immediately
      console.log('Session start time calculation failed, starting video immediately')
      startVideo()
      return
    }
    
    console.log(`Setting session start timeout for ${Math.ceil(millisecondsUntilStart / 1000)}s from now`)
    
    // Set a single timeout for the exact session start time
    sessionStartTimeout = setTimeout(() => {
      console.log('Session start timeout triggered, starting video')
      if (!videoStarted.value) {
        startVideo()
      }
      sessionStartTimeout = null
    }, millisecondsUntilStart)
    
    // Add a small buffer timeout as fallback (1 second after the main timeout)
    fallbackTimeout = setTimeout(() => {
      if (!videoStarted.value && sessionStartTimeout === null) {
        console.log('Fallback timeout triggered, starting video')
        startVideo()
      }
    }, millisecondsUntilStart + 1000)
    
    // Add a safety timeout (5 seconds after the main timeout) to prevent getting stuck
    safetyTimeout = setTimeout(() => {
      if (!videoStarted.value) {
        console.log('Safety timeout triggered, force starting video')
        forceStartVideo()
      }
    }, millisecondsUntilStart + 5000)
    
    // Start countdown display (for UI purposes)
    sessionStartCountdown.value = Math.ceil(millisecondsUntilStart / 1000) // Convert to seconds
    
    sessionStartTimer = setInterval(() => {
      if (videoStarted.value) {
        // Video has started, clear timer
        clearInterval(sessionStartTimer)
        sessionStartTimer = null
        return
      }
      
      sessionStartCountdown.value--
      
      // Emit countdown update
      emit('countdown-update', sessionStartCountdown.value)
      
      if (sessionStartCountdown.value <= 0) {
        clearInterval(sessionStartTimer)
        sessionStartTimer = null
        
        // Ensure video starts when countdown reaches 0
        if (!videoStarted.value && sessionStartTimeout) {
          console.log('Countdown reached 0, clearing timeout and starting video immediately')
          clearTimeout(sessionStartTimeout)
          sessionStartTimeout = null
          startVideo()
        }
      }
    }, 1000)
    
    // Start periodic accuracy check
    startTimeAccuracyCheck()
    
  } catch (error) {
    console.error('Error starting session countdown:', error)
  }
}

// Function to handle session time updates
const handleSessionTimeUpdate = () => {
  // Clear existing timers first
  clearSessionTimers()
  
  // Reset video state
  videoStarted.value = false
  sessionStarted.value = false
  
  // Start new countdown if needed
  if (props.session && !props.isSessionEnded && isSessionNotStarted.value) {
    startSessionCountdown()
  }
}

// Start video when session begins
const startVideo = () => {
  if (props.isSessionEnded || videoStarted.value) return
  
  try {
    console.log('Starting video...')
    console.log('Before update - videoStarted:', videoStarted.value, 'session.is_started:', props.session?.is_started)
    
    // Set flag to prevent multiple calls
    videoStarted.value = true
    
    // Set session started flag for better reactivity
    sessionStarted.value = true
    
    console.log('After update - videoStarted:', videoStarted.value, 'sessionStarted:', sessionStarted.value)
    
    // Clear all timers
    clearSessionTimers()
    
    // Emit session started event
    emit('session-started', {
      videoStarted: videoStarted.value,
      sessionStarted: sessionStarted.value
    })
    
  } catch (error) {
    console.error('Error starting video:', error)
    // Reset flags on error
    videoStarted.value = false
    sessionStarted.value = false
  }
}

// Format countdown timer for display
const formatCountdown = (seconds) => {
  if (!seconds || seconds <= 0) return 'Starting now...'
  
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  
  if (minutes > 0) {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }
  
  return `${remainingSeconds}s`
}

// Reset video state
const resetVideoState = () => {
  videoStarted.value = false
  sessionStarted.value = false
  clearSessionTimers()
}

// Watch for session changes to start countdown when needed
watch(() => props.session, (newSession) => {
  if (newSession && !props.isSessionEnded && isSessionNotStarted.value && !videoStarted.value) {
    // Handle session time updates
    handleSessionTimeUpdate()
  }
}, { immediate: false })

// Watch for about to start state
watch(isSessionAboutToStart, (isAboutToStart) => {
  if (isAboutToStart) {
    emit('about-to-start', true)
  }
})

// Lifecycle
onMounted(() => {
  // Start the session countdown timer if session hasn't started and video hasn't started
  if (props.session && !props.isSessionEnded && isSessionNotStarted.value && !videoStarted.value) {
    startSessionCountdown()
  }
})

onUnmounted(() => {
  clearSessionTimers()
})

// Expose methods and state for parent component
defineExpose({
  resetVideoState,
  startSessionCountdown,
  getTimeUntilStart,
  formatCountdown,
  sessionStartCountdown: computed(() => sessionStartCountdown.value),
  videoStarted: computed(() => videoStarted.value),
  sessionStarted: computed(() => sessionStarted.value),
  isSessionNotStarted,
  isSessionAboutToStart,
  shouldStartVideo
})
</script>
